import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { alterarSituacao, listarParticipantes, opcoesDeFiltro } from '../api/participantes';
import type { Criterios, PaginaDeParticipantes, Participante, TurmaDaOpcao } from '../api/participantes';
import { formatarMatricula } from '../matricula';
import { Aviso } from './Aviso';
import type { AvisoDaTela } from './Aviso';
import { CadastroDeParticipante } from './CadastroDeParticipante';
import { ConfirmacaoDeSituacao } from './ConfirmacaoDeSituacao';
import { EdicaoDeDados } from './EdicaoDeDados';
import { ImportacaoDePlanilha } from './ImportacaoDePlanilha';
import { MenuDeAcoes } from './MenuDeAcoes';
import type { Acao } from './MenuDeAcoes';
import { NovoCodigo } from './NovoCodigo';
import { SeloDeConexao } from './SeloDeConexao';

/** Pausa depois da última tecla antes de buscar (`DP-PT-018`). */
const PAUSA_DA_BUSCA_MS = 300;
/** Quantos botões de página aparecem em volta da atual. */
const JANELA_DE_PAGINAS = 5;
/** Releitura da página atual enquanto a aba do navegador está visível (`DP-PT-013`). */
const RELEITURA_MS = 15_000;

type AcaoDeSituacao = 'inativar' | 'reativar';

/** A janela aberta por cima da tela: no máximo uma por vez. */
type Janela =
    | { tipo: 'situacao'; participante: Participante; acao: AcaoDeSituacao }
    | { tipo: 'editar'; participante: Participante }
    | { tipo: 'codigo'; participante: Participante }
    | { tipo: 'cadastro' }
    | { tipo: 'importacao' };

const RESULTADO: Record<AcaoDeSituacao, { situacao: Participante['situacao']; sucesso: string; erro: string }> = {
    inativar: {
        situacao: 'inativo',
        sucesso: 'Participante inativado(a) com sucesso.',
        erro: 'Não foi possível inativar o participante.',
    },
    reativar: {
        situacao: 'ativo',
        sucesso: 'Participante reativado(a) com sucesso.',
        erro: 'Não foi possível reativar o participante.',
    },
};

type Carga = { tipo: 'carregando' } | { tipo: 'erro' } | { tipo: 'pronto'; dados: PaginaDeParticipantes };

interface Filtros {
    turma: string;
    /** `""`, `sem_grupo` ou `<turma>|<grupo>`: o id do grupo se repete entre turmas. */
    grupo: string;
    papel: string;
    conexao: string;
    situacao: string;
}

const SEM_FILTRO: Filtros = { turma: '', grupo: '', papel: '', conexao: '', situacao: '' };

const doisDigitos = (valor: number) => String(valor).padStart(2, '0');

const MINUTO = 60_000;
const HORA = 60 * MINUTO;

/**
 * Relativo como no modelo (`DP-PT-022`): "Agora", "Há N min", "Há N h"; passado um dia,
 * `dd/mm/aaaa hh:mm`. Sem conexão registrada, o estado informativo (`RN-PT-023`).
 */
function formatarAcesso(valor: string | null): string {
    if (!valor) return 'Nunca acessou';
    const data = new Date(valor);
    const passado = Date.now() - data.getTime();
    if (passado < MINUTO) return 'Agora';
    if (passado < HORA) return `Há ${Math.floor(passado / MINUTO)} min`;
    if (passado < 24 * HORA) return `Há ${Math.floor(passado / HORA)} h`;
    return (
        `${doisDigitos(data.getDate())}/${doisDigitos(data.getMonth() + 1)}/${data.getFullYear()} `
        + `${doisDigitos(data.getHours())}:${doisDigitos(data.getMinutes())}`
    );
}

function criterios(busca: string, filtros: Filtros, pagina: number): Criterios {
    // Escolher um grupo já gravou a turma dele em `filtros.turma` (`escolherGrupo`).
    const grupoId = filtros.grupo.includes('|') ? filtros.grupo.split('|')[1] : '';
    return {
        busca: busca || undefined,
        turma_id: filtros.turma || undefined,
        grupo_id: filtros.grupo === 'sem_grupo' ? 'sem_grupo' : grupoId || undefined,
        papel: (filtros.papel || undefined) as Criterios['papel'],
        conexao: (filtros.conexao || undefined) as Criterios['conexao'],
        situacao: (filtros.situacao || undefined) as Criterios['situacao'],
        pagina,
    };
}

function paginasVisiveis(atual: number, total: number): number[] {
    const inicio = Math.max(1, Math.min(atual - Math.floor(JANELA_DE_PAGINAS / 2), total - JANELA_DE_PAGINAS + 1));
    const fim = Math.min(total, inicio + JANELA_DE_PAGINAS - 1);
    return Array.from({ length: fim - inicio + 1 }, (_, indice) => inicio + indice);
}

interface LinhaProps {
    participante: Participante;
    menuAberto: boolean;
    aoAlternarMenu: () => void;
    aoFecharMenu: () => void;
    aoEscolher: (acao: Acao) => void;
}

function LinhaDoParticipante({ participante, menuAberto, aoAlternarMenu, aoFecharMenu, aoEscolher }: LinhaProps) {
    return (
        <tr>
            <td>{participante.turma_id}</td>
            <td>{participante.grupo_id ? participante.grupo_nome ?? participante.grupo_id : 'Sem grupo'}</td>
            <td className="matricula">{formatarMatricula(participante.matricula)}</td>
            <td>
                {participante.papel === 'lider'
                    ? <span className="selo-lider">Líder</span>
                    : <span>Participante</span>}
            </td>
            <td>
                <span className={`selo-situacao selo-${participante.situacao}`}>
                    {participante.situacao === 'ativo' ? 'Ativo' : 'Inativo'}
                </span>
                {participante.bloqueado && <span className="selo-situacao selo-bloqueado">Bloqueado</span>}
            </td>
            <td>
                <SeloDeConexao conexao={participante.conexao} />
            </td>
            <td>{participante.tablet ?? 'Não atribuído'}</td>
            <td className="numero">{formatarAcesso(participante.ultimo_acesso)}</td>
            <td className="coluna-acao">
                <MenuDeAcoes
                    participante={participante}
                    aberto={menuAberto}
                    aoAlternar={aoAlternarMenu}
                    aoFechar={aoFecharMenu}
                    aoEscolher={aoEscolher}
                />
            </td>
        </tr>
    );
}

interface ParticipantesPainelProps {
    csrfToken?: string;
}

export function ParticipantesPainel({ csrfToken = '' }: ParticipantesPainelProps = {}) {
    const [turmas, setTurmas] = useState<TurmaDaOpcao[]>([]);
    const [textoDaBusca, setTextoDaBusca] = useState('');
    const [busca, setBusca] = useState('');
    const [filtros, setFiltros] = useState<Filtros>(SEM_FILTRO);
    const [pagina, setPagina] = useState(1);
    const [carga, setCarga] = useState<Carga>({ tipo: 'carregando' });
    // Só a resposta do pedido mais recente vale: uma busca antiga que chega depois é descartada.
    const ultimoPedido = useRef(0);
    // Mudar este número relê a página atual com os mesmos critérios.
    const [releitura, setReleitura] = useState(0);
    // A releitura periódica não derruba a tela: se falhar, a tabela que já está ali fica.
    const releituraSilenciosa = useRef(false);
    const [menuAberto, setMenuAberto] = useState<string | null>(null);
    const [janela, setJanela] = useState<Janela | null>(null);
    // O botão "Adicionar Participante" vai para o canto do título da seção, como no modelo.
    const [espacoDoTitulo, setEspacoDoTitulo] = useState<HTMLElement | null>(null);
    const [processando, setProcessando] = useState(false);
    const [aviso, setAviso] = useState<AvisoDaTela | null>(null);
    const fecharAviso = useCallback(() => setAviso(null), []);
    const fecharMenu = useCallback(() => setMenuAberto(null), []);
    const fecharJanela = useCallback(() => setJanela(null), []);

    useEffect(() => {
        setEspacoDoTitulo(document.getElementById('acoes-da-secao'));
    }, []);

    /** Depois de uma escrita confirmada: aviso de sucesso e releitura (`RI-PT-052`). */
    const confirmado = useCallback((texto: string) => {
        setAviso({ tipo: 'sucesso', texto });
        setReleitura((atual) => atual + 1);
    }, []);

    useEffect(() => {
        opcoesDeFiltro().then(({ turmas: lista }) => setTurmas(lista)).catch(() => setTurmas([]));
    }, []);

    // A busca aplicada também mora num ref: o temporizador precisa compará-la sem reagendar a cada troca.
    const buscaAplicada = useRef('');
    useEffect(() => {
        const espera = setTimeout(() => {
            const limpa = textoDaBusca.trim();
            if (limpa === buscaAplicada.current) return;
            buscaAplicada.current = limpa;
            setBusca(limpa);
            setPagina(1);
        }, PAUSA_DA_BUSCA_MS);
        return () => clearTimeout(espera);
    }, [textoDaBusca]);

    useEffect(() => {
        const pedido = ++ultimoPedido.current;
        const silenciosa = releituraSilenciosa.current;
        releituraSilenciosa.current = false;
        listarParticipantes(criterios(busca, filtros, pagina))
            .then((dados) => {
                if (pedido === ultimoPedido.current) setCarga({ tipo: 'pronto', dados });
            })
            .catch(() => {
                if (pedido !== ultimoPedido.current) return;
                setCarga((atual) => (silenciosa && atual.tipo === 'pronto' ? atual : { tipo: 'erro' }));
            });
    }, [busca, filtros, pagina, releitura]);

    // O que outro operador ou o login no tablet mudaram aparece sem recarregar (`RI-PT-019`).
    useEffect(() => {
        const intervalo = setInterval(() => {
            if (document.visibilityState !== 'visible') return;
            releituraSilenciosa.current = true;
            setReleitura((atual) => atual + 1);
        }, RELEITURA_MS);
        return () => clearInterval(intervalo);
    }, []);

    async function confirmarSituacao() {
        if (janela?.tipo !== 'situacao' || processando) return;
        const { participante, acao } = janela;
        setProcessando(true);
        try {
            await alterarSituacao({ participante, situacao: RESULTADO[acao].situacao, csrfToken });
            confirmado(RESULTADO[acao].sucesso);
        } catch {
            setAviso({ tipo: 'erro', texto: RESULTADO[acao].erro });
        } finally {
            setProcessando(false);
            setJanela(null);
        }
    }

    function escolher(participante: Participante, acao: Acao) {
        setMenuAberto(null);
        if (acao === 'editar' || acao === 'codigo') setJanela({ tipo: acao, participante });
        else setJanela({ tipo: 'situacao', participante, acao });
    }

    // Todo critério novo volta à primeira página (`RI-PT-030`).
    function filtrar(mudanca: Partial<Filtros>) {
        setFiltros((atual) => ({ ...atual, ...mudanca }));
        setPagina(1);
    }

    function escolherGrupo(valor: string) {
        const turmaDoGrupo = valor.includes('|') ? valor.split('|')[0] : filtros.turma;
        filtrar({ grupo: valor, turma: turmaDoGrupo });
    }

    function limpar() {
        setTextoDaBusca('');
        buscaAplicada.current = '';
        setBusca('');
        setFiltros(SEM_FILTRO);
        setPagina(1);
    }

    const botoesDaSecao = (
        <>
            <button type="button" className="botao-importar" onClick={() => setJanela({ tipo: 'importacao' })}>
                Importar planilha
            </button>
            <button type="button" className="botao-adicionar" onClick={() => setJanela({ tipo: 'cadastro' })}>
                <span aria-hidden="true">+</span> Adicionar Participante
            </button>
        </>
    );
    const turmasDoGrupo = filtros.turma ? turmas.filter((turma) => turma.id === filtros.turma) : turmas;
    const dados = carga.tipo === 'pronto' ? carga.dados : null;

    return (
        <div className="painel-participantes">
            {aviso && <Aviso aviso={aviso} aoFechar={fecharAviso} />}
            {janela?.tipo === 'situacao' && (
                <ConfirmacaoDeSituacao
                    participante={janela.participante}
                    acao={janela.acao}
                    processando={processando}
                    aoCancelar={fecharJanela}
                    aoConfirmar={() => void confirmarSituacao()}
                />
            )}
            {janela?.tipo === 'cadastro' && (
                <CadastroDeParticipante
                    turmas={turmas}
                    turmaInicial={filtros.turma}
                    csrfToken={csrfToken}
                    aoCadastrar={() => confirmado('Participante cadastrado(a) com sucesso.')}
                    aoFechar={fecharJanela}
                />
            )}
            {janela?.tipo === 'importacao' && (
                <ImportacaoDePlanilha
                    turmas={turmas}
                    turmaInicial={filtros.turma}
                    csrfToken={csrfToken}
                    aoImportar={(quantidade) =>
                        confirmado(`${quantidade} ${quantidade === 1 ? 'participante importado' : 'participantes importados'} com sucesso.`)
                    }
                    aoFechar={fecharJanela}
                />
            )}
            {janela?.tipo === 'editar' && (
                <EdicaoDeDados
                    participante={janela.participante}
                    csrfToken={csrfToken}
                    aoSalvar={() => {
                        setJanela(null);
                        confirmado('Dados atualizados com sucesso.');
                    }}
                    aoFechar={fecharJanela}
                />
            )}
            {janela?.tipo === 'codigo' && (
                <NovoCodigo
                    participante={janela.participante}
                    csrfToken={csrfToken}
                    aoGerar={() => confirmado('Novo código gerado com sucesso.')}
                    aoFechar={fecharJanela}
                />
            )}
            {espacoDoTitulo ? createPortal(botoesDaSecao, espacoDoTitulo) : botoesDaSecao}
            <div className="barra-participantes">
                <input
                    type="search"
                    className="busca-participantes"
                    aria-label="Buscar participante"
                    placeholder="Buscar por nome, matrícula ou grupo"
                    value={textoDaBusca}
                    onChange={(evento) => setTextoDaBusca(evento.target.value)}
                />
                <div className="filtros-participantes">
                    <select aria-label="Turma" value={filtros.turma} onChange={(evento) => filtrar({ turma: evento.target.value, grupo: '' })}>
                        <option value="">Turma</option>
                        {turmas.map((turma) => (
                            <option key={turma.id} value={turma.id}>{turma.nome ? `${turma.id} · ${turma.nome}` : turma.id}</option>
                        ))}
                    </select>
                    <select aria-label="Grupo" value={filtros.grupo} onChange={(evento) => escolherGrupo(evento.target.value)}>
                        <option value="">Grupo</option>
                        <option value="sem_grupo">Sem grupo</option>
                        {turmasDoGrupo.flatMap((turma) => turma.grupos.map((grupo) => (
                            <option key={`${turma.id}|${grupo.id}`} value={`${turma.id}|${grupo.id}`}>
                                {`${turma.id} · ${grupo.nome ?? grupo.id}`}
                            </option>
                        )))}
                    </select>
                    <select aria-label="Papel" value={filtros.papel} onChange={(evento) => filtrar({ papel: evento.target.value })}>
                        <option value="">Papel</option>
                        <option value="lider">Líder</option>
                        <option value="participante">Participante</option>
                    </select>
                    <select aria-label="Conexão" value={filtros.conexao} onChange={(evento) => filtrar({ conexao: evento.target.value })}>
                        <option value="">Conexão</option>
                        <option value="online">Online</option>
                        <option value="instavel">Instável</option>
                        <option value="offline">Offline</option>
                    </select>
                    <select aria-label="Situação" value={filtros.situacao} onChange={(evento) => filtrar({ situacao: evento.target.value })}>
                        <option value="">Situação</option>
                        <option value="ativo">Ativo</option>
                        <option value="inativo">Inativo</option>
                    </select>
                    <button className="botao-texto" type="button" onClick={limpar}>Limpar filtros</button>
                </div>
            </div>

            {carga.tipo === 'carregando' && <p className="mensagem-login">Carregando participantes…</p>}
            {carga.tipo === 'erro' && (
                <p className="mensagem-login aviso-painel" role="alert">Não foi possível carregar os participantes.</p>
            )}
            {dados && (
                <>
                    <p className="contador-participantes">
                        {dados.total === 1 ? '1 participante' : `${dados.total} participantes`}
                    </p>
                    {dados.itens.length === 0 ? (
                        <p className="estado-vazio">Nenhum participante encontrado.</p>
                    ) : (
                        <>
                            <table className="tabela-jogadores">
                                <thead>
                                    <tr>
                                        <th scope="col">Turma</th>
                                        <th scope="col">Grupo</th>
                                        <th scope="col">Matrícula</th>
                                        <th scope="col">Papel</th>
                                        <th scope="col">Situação</th>
                                        <th scope="col">Conexão</th>
                                        <th scope="col">Tablet</th>
                                        <th scope="col">Último acesso</th>
                                        <th scope="col" className="coluna-acao">Ação</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {dados.itens.map((participante) => (
                                        <LinhaDoParticipante
                                            key={participante.id}
                                            participante={participante}
                                            menuAberto={menuAberto === participante.id}
                                            aoAlternarMenu={() => setMenuAberto((atual) => (atual === participante.id ? null : participante.id))}
                                            aoFecharMenu={fecharMenu}
                                            aoEscolher={(acao) => escolher(participante, acao)}
                                        />
                                    ))}
                                </tbody>
                            </table>
                            <nav className="paginacao" aria-label="Paginação">
                                <button
                                    type="button"
                                    aria-label="Página anterior"
                                    disabled={pagina <= 1}
                                    onClick={() => setPagina(pagina - 1)}
                                >
                                    ‹
                                </button>
                                {paginasVisiveis(pagina, dados.paginas).map((numero) => (
                                    <button
                                        key={numero}
                                        type="button"
                                        aria-label={`Página ${numero}`}
                                        aria-current={numero === pagina ? 'page' : undefined}
                                        onClick={() => setPagina(numero)}
                                    >
                                        {numero}
                                    </button>
                                ))}
                                <button
                                    type="button"
                                    aria-label="Próxima página"
                                    disabled={pagina >= dados.paginas}
                                    onClick={() => setPagina(pagina + 1)}
                                >
                                    ›
                                </button>
                            </nav>
                        </>
                    )}
                </>
            )}
        </div>
    );
}
